import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck, Loader2, Crown, ArrowLeft, Search, Users, Ban, ShieldOff,
  CircleCheck, Activity, CreditCard,
} from "lucide-react";
import { SimLayout } from "@/sim/SimLayout";
import { useSimAuth } from "@/sim/SimGate";
import { getSimUsers, setUserAdmin, setUserBlocked } from "@/sim/lib";
import { cn } from "@/lib/utils";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return "—"; }
}

function Stat({ icon: Icon, label, value, color = "#F5F5F5" }) {
  return (
    <div className="sim-card rounded-xl p-4">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3.5 h-3.5 text-[#C2C5CE]" />
        <span className="sim-mono text-[10px] text-[#C2C5CE] uppercase tracking-wider">{label}</span>
      </div>
      <p className="sim-head text-2xl font-bold" style={{ color }}>{value}</p>
    </div>
  );
}

export default function SimAdmin() {
  const navigate = useNavigate();
  const { user } = useSimAuth();
  const [rows, setRows] = useState([]);
  const [bootstrap, setBootstrap] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(null); // user_id currently updating

  const load = async () => {
    try {
      const d = await getSimUsers();
      setRows(d.users || []);
      setBootstrap(d.bootstrap_admins || []);
    } catch {
      toast.error("Could not load users");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? rows.filter((r) => (r.email || "").toLowerCase().includes(q) || (r.name || "").toLowerCase().includes(q))
      : rows;
    return [...list].sort((a, b) => new Date(b.last_login_at || 0) - new Date(a.last_login_at || 0));
  }, [rows, query]);

  const stats = useMemo(() => ({
    total: rows.length,
    admins: rows.filter((r) => r.is_admin).length,
    blocked: rows.filter((r) => r.blocked).length,
    paying: rows.filter((r) => r.subscription_status === "active").length,
    sims: rows.reduce((s, r) => s + (r.sim_count || 0), 0),
  }), [rows]);

  const toggleAdmin = async (r) => {
    setBusy(r.user_id);
    try {
      await setUserAdmin(r.user_id, !r.is_admin);
      toast.success(!r.is_admin ? `${r.email} is now an admin` : `Removed admin from ${r.email}`);
      load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };

  const toggleBlock = async (r) => {
    setBusy(r.user_id);
    try {
      await setUserBlocked(r.user_id, !r.blocked);
      toast.success(!r.blocked ? `Blocked ${r.email}` : `Restored access for ${r.email}`);
      load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };

  return (
    <SimLayout maxWidth="max-w-5xl">
      <button onClick={() => navigate("/sim")} className="sim-mono text-[11px] text-[#C2C5CE] hover:text-[#F5F5F5] flex items-center gap-1.5 mb-5 transition-colors">
        <ArrowLeft className="w-3 h-3" /> Back to simulator
      </button>

      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-[#00F0FF]/10 border border-[#00F0FF]/30 flex items-center justify-center">
          <ShieldCheck className="w-5 h-5 text-[#00F0FF]" />
        </div>
        <div>
          <h1 className="sim-head text-2xl font-extrabold text-[#F5F5F5]">User Access</h1>
          <p className="text-[#C2C5CE] text-sm">Every Google account that signs in is auto-approved and captured here.</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6" data-testid="sim-admin-stats">
        <Stat icon={Users} label="Total users" value={stats.total} />
        <Stat icon={Crown} label="Admins" value={stats.admins} color="#F59E0B" />
        <Stat icon={CreditCard} label="Paying" value={stats.paying} color="#10B981" />
        <Stat icon={Ban} label="Blocked" value={stats.blocked} color="#EF4444" />
        <Stat icon={Activity} label="Sims run" value={stats.sims} color="#00F0FF" />
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="w-4 h-4 text-[#9AA0AC] absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          data-testid="sim-admin-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by email or name"
          className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg pl-9 pr-3 h-11 text-[#F5F5F5] sim-mono text-sm placeholder:text-[#8E929C] focus:border-[#00F0FF]/50 focus:outline-none transition-colors"
        />
      </div>

      <p className="sim-mono text-[11px] text-[#C2C5CE] uppercase tracking-wider mb-3">Users · {filtered.length}</p>

      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 text-[#00F0FF] animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="sim-card rounded-xl p-10 text-center">
          <Users className="w-7 h-7 text-[#8E929C] mx-auto mb-3" />
          <p className="text-[#C2C5CE] text-sm">No users yet. People appear here the moment they sign in.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="sim-admin-users">
          {filtered.map((r) => {
            const isBootstrap = bootstrap.includes(r.email);
            const isSelf = user?.email === r.email;
            const locked = isBootstrap || isSelf;
            const working = busy === r.user_id;
            return (
              <div key={r.user_id} data-testid={`sim-user-${r.email}`}
                className={cn("sim-card rounded-lg px-4 py-3 flex items-center gap-3", r.blocked && "opacity-70")}>
                <div className="w-9 h-9 rounded-full bg-[#1A1A1A] border border-[#27272A] flex items-center justify-center overflow-hidden shrink-0">
                  {r.picture ? (
                    <img src={r.picture} alt={r.name || r.email} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                  ) : (
                    <span className="sim-mono text-xs text-[#C2C5CE]">{(r.email || "?")[0].toUpperCase()}</span>
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-[#F5F5F5] text-sm font-semibold truncate">{r.name || r.email.split("@")[0]}</p>
                    {r.is_admin && <span className="sim-mono text-[9px] text-[#F59E0B] flex items-center gap-1 shrink-0"><Crown className="w-2.5 h-2.5" /> ADMIN</span>}
                    {isBootstrap && <span className="sim-mono text-[9px] text-[#9AA0AC] shrink-0">OWNER</span>}
                    {isSelf && <span className="sim-mono text-[9px] text-[#00F0FF] shrink-0">YOU</span>}
                    {r.blocked && <span className="sim-mono text-[9px] text-[#EF4444] shrink-0">BLOCKED</span>}
                  </div>
                  <p className="text-[#C2C5CE] text-xs truncate sim-mono">{r.email}</p>
                  <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5 mt-1 sim-mono text-[10px] text-[#9AA0AC]">
                    <span>First: {fmtDate(r.first_login_at)}</span>
                    <span>Last: {fmtDate(r.last_login_at)}</span>
                    <span className="text-[#00F0FF]">{r.sim_count || 0} sims</span>
                    {r.subscription_status === "active" ? (
                      <span className="text-[#10B981] flex items-center gap-1" data-testid={`sim-user-plan-${r.email}`}>
                        <CreditCard className="w-2.5 h-2.5" /> PRO · $99/mo
                      </span>
                    ) : r.subscription_status === "past_due" ? (
                      <span className="text-[#EF4444]" data-testid={`sim-user-plan-${r.email}`}>PAYMENT FAILED</span>
                    ) : r.card_on_file ? (
                      <span className="text-[#F59E0B]" data-testid={`sim-user-plan-${r.email}`}>
                        Trial {Math.min(r.trial_sims_used || 0, 3)}/3 · card on file
                      </span>
                    ) : (
                      <span data-testid={`sim-user-plan-${r.email}`}>No card yet</span>
                    )}
                  </div>
                </div>

                {locked ? (
                  <span className="sim-mono text-[10px] text-[#8E929C] shrink-0">locked</span>
                ) : (
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      data-testid={`sim-user-admin-${r.email}`}
                      onClick={() => toggleAdmin(r)}
                      disabled={working}
                      title={r.is_admin ? "Revoke admin" : "Make admin"}
                      className={cn("h-8 px-2.5 rounded-lg sim-mono text-[10px] font-bold flex items-center gap-1 border transition-colors disabled:opacity-50",
                        r.is_admin
                          ? "border-[#F59E0B]/40 text-[#F59E0B] hover:bg-[#F59E0B]/10"
                          : "border-[#27272A] text-[#C2C5CE] hover:text-[#F59E0B] hover:border-[#F59E0B]/40")}
                    >
                      {working ? <Loader2 className="w-3 h-3 animate-spin" /> : <Crown className="w-3 h-3" />}
                      {r.is_admin ? "Admin" : "Make admin"}
                    </button>
                    <button
                      data-testid={`sim-user-block-${r.email}`}
                      onClick={() => toggleBlock(r)}
                      disabled={working}
                      title={r.blocked ? "Restore access" : "Block user"}
                      className={cn("h-8 px-2.5 rounded-lg sim-mono text-[10px] font-bold flex items-center gap-1 border transition-colors disabled:opacity-50",
                        r.blocked
                          ? "border-[#10B981]/40 text-[#10B981] hover:bg-[#10B981]/10"
                          : "border-[#27272A] text-[#C2C5CE] hover:text-[#EF4444] hover:border-[#EF4444]/40")}
                    >
                      {working ? <Loader2 className="w-3 h-3 animate-spin" /> : r.blocked ? <CircleCheck className="w-3 h-3" /> : <ShieldOff className="w-3 h-3" />}
                      {r.blocked ? "Unblock" : "Block"}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </SimLayout>
  );
}
